import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Bot, Shield, Key, ArrowLeft, Award, Zap, Database, Terminal, Copy, Check } from 'lucide-react'

interface AgentBasic {
  id: string
  agent_name: string
  agent_description: string | null
  type: string
  status: string
  tenant_name: string
  is_public: boolean
  capabilities?: string[]
  data_access?: string[]
  mcp_schema_url?: string | null
  risk_score?: number | null
  last_audit_date?: string | null
  compliance_badges?: Array<{ badge_name: string; framework: string; issued_date: string; expiry_date?: string }>
}

const TYPE_COLORS: Record<string, string> = {
  agent: 'bg-blue-100 text-blue-800',
  mcp_server: 'bg-purple-100 text-purple-800',
  service_account: 'bg-green-100 text-green-800',
  human: 'bg-gray-100 text-gray-800'
}

const TYPE_LABELS: Record<string, string> = {
  agent: 'AI Agent',
  mcp_server: 'MCP Server',
  service_account: 'Service Account',
  human: 'Human Operator'
}

type ProfileTab = 'overview' | 'capabilities' | 'trust' | 'query-api'

export default function AgentProfilePage() {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const [agent, setAgent] = useState<AgentBasic | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!agentId) return
    loadAgent()
  }, [agentId])

  async function loadAgent() {
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`/api/registry/query?agentId=${agentId}&tier=basic`)
      if (!res.ok) {
        if (res.status === 404) throw new Error('Agent not found')
        throw new Error('Failed to load agent')
      }
      const data = await res.json()
      setAgent(data.agent)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function copyApiSnippet() {
    const snippet = `# Free query
curl /api/registry/query?agentId=${agentId}&tier=basic

# Metered (requires API key)
curl -H "Authorization: Bearer tc_your_key" \\
  /api/registry/query?agentId=${agentId}&tier=capability

# MCP tool call
curl -X POST /api/registry/mcp \\
  -H "Authorization: Bearer tc_your_key" \\
  -d '{"jsonrpc":"2.0","method":"tools/call","params":{"name":"get_agent_profile","arguments":{"agent_id":"${agentId}"}},"id":1}'`
    navigator.clipboard.writeText(snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const TABS: { key: ProfileTab; label: string; icon: React.ComponentType<any> }[] = [
    { key: 'overview', label: 'Overview', icon: Bot },
    { key: 'capabilities', label: 'Capabilities', icon: Zap },
    { key: 'trust', label: 'Trust', icon: Shield },
    { key: 'query-api', label: 'Query API', icon: Terminal }
  ]

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">Loading agent profile...</p>
    </div>
  )

  if (error || !agent) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 mb-4">{error || 'Agent not found'}</p>
        <button onClick={() => navigate('/registry')} className="text-blue-600 hover:underline flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Registry
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/registry')}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-4"
          >
            <ArrowLeft className="h-4 w-4" /> Agent Registry
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Bot className="h-9 w-9 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">{agent.agent_name}</h1>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[agent.type] ?? 'bg-gray-100 text-gray-800'}`}>
                  {TYPE_LABELS[agent.type] ?? agent.type}
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {agent.status}
                </span>
              </div>
              <p className="text-gray-500 text-sm mt-1">{agent.tenant_name}</p>
              {agent.agent_description && (
                <p className="text-gray-700 mt-2">{agent.agent_description}</p>
              )}
            </div>
            <a
              href="/admin"
              className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex-shrink-0"
            >
              <Key className="h-4 w-4" /> Get API Key
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 px-4">
            <nav className="flex space-x-6">
              {TABS.map(tab => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm gap-2 ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <span className="ml-2 text-gray-600">{TYPE_LABELS[agent.type] ?? agent.type}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="ml-2 text-gray-600">{agent.status}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Organization:</span>
                    <span className="ml-2 text-gray-600">{agent.tenant_name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Publicly listed:</span>
                    <span className="ml-2 text-gray-600">{agent.is_public ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                {agent.agent_description && (
                  <div>
                    <p className="font-medium text-gray-700 text-sm mb-1">Description</p>
                    <p className="text-gray-600 text-sm">{agent.agent_description}</p>
                  </div>
                )}
                <div className="bg-blue-50 rounded-lg p-4 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Query this agent</p>
                  <p className="text-blue-700">
                    Use the <strong>Query API</strong> tab to get detailed capabilities, trust scores, and compliance status.
                    Basic tier is free — advanced tiers require an API key.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'capabilities' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  Capability data requires a <strong>capability tier</strong> query ($0.03). Sign up for an API key to access.
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Zap className="h-5 w-5" />
                  <span className="text-sm">Capabilities and data access scope available via API query.</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Database className="h-5 w-5" />
                  <span className="text-sm">MCP schema URL available at capability tier.</span>
                </div>
                <a
                  href="/admin"
                  className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm font-medium"
                >
                  <Key className="h-4 w-4" /> Get API Key to Query Capabilities
                </a>
              </div>
            )}

            {activeTab === 'trust' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  Trust data (risk score, audit dates, compliance badges) requires a <strong>trust tier</strong> query ($0.15).
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Shield className="h-5 w-5" />
                  <span className="text-sm">Risk score and last audit date available at trust tier.</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Award className="h-5 w-5" />
                  <span className="text-sm">Compliance badges (SOC 2, ISO 27001, GDPR, etc.) available at trust tier.</span>
                </div>
                <a
                  href="/admin"
                  className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm font-medium"
                >
                  <Key className="h-4 w-4" /> Get API Key to Access Trust Data
                </a>
              </div>
            )}

            {activeTab === 'query-api' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Use these examples to query this agent's profile:</p>
                <div className="relative">
                  <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-xs overflow-x-auto leading-relaxed">
                    <div className="text-gray-500"># Free (no auth required)</div>
                    <div>curl /api/registry/query?agentId={agentId}&tier=basic</div>
                    <div className="mt-3 text-gray-500"># Capability tier ($0.03)</div>
                    <div>curl -H "Authorization: Bearer tc_your_key" \</div>
                    <div>  /api/registry/query?agentId={agentId}&tier=capability</div>
                    <div className="mt-3 text-gray-500"># Trust tier ($0.15)</div>
                    <div>curl -H "Authorization: Bearer tc_your_key" \</div>
                    <div>  /api/registry/query?agentId={agentId}&tier=trust</div>
                    <div className="mt-3 text-gray-500"># MCP tool call</div>
                    <div>curl -X POST /api/registry/mcp \</div>
                    <div>  -H "Authorization: Bearer tc_your_key" \</div>
                    <div>  -d '{`{"jsonrpc":"2.0","method":"tools/call","params":{"name":"get_agent_profile","arguments":{"agent_id":"${agentId}"}},"id":1}`}'</div>
                  </div>
                  <button
                    onClick={copyApiSnippet}
                    className="absolute top-2 right-2 text-gray-400 hover:text-white bg-gray-700 rounded p-1"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <a
                  href="/admin"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  <Key className="h-4 w-4" /> Get API Key
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
